import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Info, ChevronDown } from 'lucide-react';
import { cn } from '../../utils';

export default function FormulaSection() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="relative overflow-hidden rounded-[2.5rem] bg-slate-900 text-white dark:bg-slate-950"
    >
      <div className="bg-primary/20 pointer-events-none absolute top-0 right-0 -mt-32 -mr-32 h-64 w-64 rounded-full blur-[100px]" />

      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="group relative z-10 flex w-full items-center justify-between px-12 py-8"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition-colors group-hover:bg-white/20">
            <Info className="text-primary h-5 w-5" />
          </div>
          <div>
            <h2 className="text-left text-xl font-extrabold tracking-tight">
              The MMEM Formula
            </h2>
            <p className="text-left text-xs font-medium text-slate-400">
              Mathematical breakdown of the encoding engine
            </p>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'h-5 w-5 text-slate-500 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <div className="relative z-10 px-12 pb-12">
              <div className="mb-10 rounded-3xl border border-white/10 bg-white/5 p-10 text-center">
                <span className="bg-gradient-to-r from-white to-white/40 bg-clip-text text-4xl font-black tracking-tighter text-transparent md:text-6xl">
                  L = (A · M · C) + (R · T) + S
                </span>
              </div>

              <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
                <div className="space-y-3">
                  <h3 className="text-primary text-xs font-black tracking-widest uppercase">
                    Encoding Triad
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-400 dark:text-slate-500">
                    Multiplicative bottleneck. Zero attention results in zero
                    encoding.
                  </p>
                </div>
                <div className="space-y-3">
                  <h3 className="text-secondary text-xs font-black tracking-widest uppercase">
                    Reinforcement
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-400 dark:text-slate-500">
                    Spaced repetition (T) acts as a force multiplier for
                    rehearsal (R).
                  </p>
                </div>
                <div className="space-y-3">
                  <h3 className="text-accent text-xs font-black tracking-widest uppercase">
                    Consolidation
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-400 dark:text-slate-500">
                    Sleep (S) is the non-negotiable biological stabilization
                    constant.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
